import React, { useContext, useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import CloseIcon from '@material-ui/icons/Close';

import cloneDeep from 'lodash/cloneDeep';

import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  SvgIcon
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import AddCircleIcon from '@material-ui/icons/AddCircle';
import SaveIcon from '@material-ui/icons/Save';
import HomeIcon from '@material-ui/icons/Home';
import EditLocationIcon from '@material-ui/icons/EditLocation';
import { LoadingOverlayWrapper } from 'components/layout';
import Layer from 'components/explore/views/Layer';
import ViewObjectDetail from 'components/explore/views/ViewObjectDetail';
import NameInput from 'components/explore/views/NameInput';
import { updateSelectedExploreView, createExploreViewLayer, saveExploreView,
  setExploreSelectedViewIsOpen, setConfirmationModalState, deleteExploreView, cancelExploreDataFetch, setExploreLocation, appNotify, setIsSideBarOpen, fetchExplorePropertyData } from 'store/actions';
import { Constants } from 'utilities/constants';
import classNames from 'classnames';
import { sendClientLog } from 'services/logService';
import { extendBoundsToObject } from 'components/explore/gmap/helpers';
import { MapRefContext } from 'components/explore/Explore';
import DuplicateViewModal from 'components/explore/views/DuplicateViewModal';
import { fitBoundsWithZoom } from 'components/explore/gmap/helpers';

const View = ({
  classes,
  user,
  name,
  selectedView,
  selectedViewLoadingState,
  deleteExploreView,
  updateSelectedExploreView,
  setExploreSelectedViewIsOpen,
  createExploreViewLayer,
  selectedViewIsDirty,
  saveExploreView,
  mapPosition,
  setExploreLocation,
  setConfirmationModalState,
  hasSelectedObject,
  cancelExploreDataFetch,
  appNotify,
  setIsSideBarOpen,
  isMobile,
  fetchExplorePropertyData
}) => {
  const mapRef = useContext(MapRefContext);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [duplicateViewModalIsOpen, setDuplicateViewModalIsOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [objectCountLimitOverrides, setObjectCountLimitOverrides] = useState(null);

  useEffect(() => {
    setObjectCountLimitOverrides(null);
  }, [selectedView?.id]);

  const handleAddLayerClick = () => {
    createExploreViewLayer();
    sendClientLog('Info',`Explore Views - User clicked Add Layer button for view id ${selectedView?.id}`);
  }

  const handleSaveClick = () => {
    saveExploreView();
    sendClientLog('Info',`Explore Views - User clicked Save button for view id ${selectedView?.id}`);
  }

  const handleDuplicateViewClick = () => {
    setDuplicateViewModalIsOpen(true);
    sendClientLog('Info',`Explore Views - User clicked Duplicate View button for view id ${selectedView?.id}`);
  }

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  }

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  }

  const handleSetPosition = () => {
    updateSelectedExploreView(mapPosition);
    sendClientLog('Info',`Explore Views - User clicked Set Position button for view id ${selectedView?.id}`);
    appNotify('Home position set', 'success');
  }

  const handleJumpToPosition = () => {
    const center = selectedView.latitude != null && selectedView.longitude != null ? { lat: selectedView.latitude, lng: selectedView.longitude } : null;
    setExploreLocation({ zoom: selectedView.zoomLevel, center, bounds: selectedView.bounds });
    sendClientLog('Info',`Explore Views - User clicked Home Position button for view id ${selectedView?.id}`);
  }

  const handleBestFitView = () => {
    closeMoreMenu();
    if (!selectedView) return;
    let bounds = new window.google.maps.LatLngBounds();
    selectedView.objects.forEach(obj => {
      if (!obj.visible || obj.needsPositionSet) return;
      bounds = extendBoundsToObject(bounds, obj, mapRef);
    })
    mapRef?.current && fitBoundsWithZoom(mapRef.current, bounds);
  }

  const handleRenameClick = (timeout = 0) => {
    closeMoreMenu();
    setTimeout(() => setIsEditingName(true), timeout);
  }

  const handleNameSave = name => {
    setIsEditingName(false);
    if (name === selectedView.name) return;
    updateSelectedExploreView({ name });
  }

  const handleDeleteView = () => {
    closeMoreMenu();
    setConfirmationModalState({
			isModalOpen: true,
      handleConfirmation: () => {
				closeConfirmationModal();
        deleteExploreView(selectedView.id);
        if (isTemp) {
          sendClientLog('Info',`Explore Views - User discarded temporary view from View menu`);
        } else {
          sendClientLog('Info',`Explore Views - User clicked and confirmed Delete on View menu for view id ${selectedView.id}`);
        }
			},
      handleCancellation: () => {
				closeConfirmationModal();
			},
      titleText: 'Confirm',
      contentText: `Are you sure you want to ${isTemp ? 'discard' : 'delete'} this view?`
		})
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  function onDragEnd(result) {
    const { destination, source, draggableId } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    if(
      destination.droppableId === source.droppableId
      && destination.index === source.index
    ) {
      return;
    }

    // handler for object drag and drop
    if (draggableId?.includes('object-')) {
      const layers = cloneDeep(selectedView.layers);

      const destinationLayerId = destination.droppableId.split('object-')[1];
      const destinationLayerIndex = layers.findIndex(layer => `${layer.id}` === `${destinationLayerId}`);

      const sourceLayerId = source.droppableId.split('object-')[1];
      const sourceLayerIndex = layers.findIndex(layer => `${layer.id}` === `${sourceLayerId}`);

      const movableItem = layers[sourceLayerIndex].objectIds.splice(source.index, 1)[0];
      layers[destinationLayerIndex].objectIds.splice(destination.index, 0, movableItem);

      const newObjects = [];
      layers.forEach(layer => {
        if (layer.objectIds.length) {
          layer.objectIds.forEach((objId, index) => {
            const obj = selectedView.objects.find(el => el.id === objId);
            newObjects.push({ ...obj, layerId: layer.id, index });
          });
        }
      })

      updateSelectedExploreView({ layers, objects: newObjects });
      // handler for layer drag and drop
    } else if (draggableId?.includes('layer-')) {
      const layers = cloneDeep(selectedView.layers);
      const movableItem = layers.splice(source.index, 1)[0];
      layers.splice(destination.index, 0, movableItem);

      layers.forEach((layer, index) => { layer.index = index });
      updateSelectedExploreView({ layers });
    }
  }

  const hasAnObject = selectedView?.objects.length > 0;
  const hasAPropertyDataObject = hasAnObject && selectedView.objects.some(obj => obj.type === Constants.ViewsObjectType.PROPERTYDATA);
  const isSystemView = selectedView?.id === 'system';
  const isTemp = selectedView?.id === 'temp';
  const isUserCreatedView = user.id === selectedView?.createdByUserId || isTemp;
  const isAdmin = user.roles.administrator;
  const { isLoading, message } = selectedViewLoadingState;

  const saveDisabled = isLoading || !hasAnObject;
  const canSave = !isSystemView && (isUserCreatedView || isAdmin);
  
  return (
    <>
      <div className={classNames(classes.viewContainer, hasSelectedObject && classes.hiddenContainer)}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <Tooltip title='Back to all Views'>
              <span>
                <IconButton
                  onClick={() => setExploreSelectedViewIsOpen(false)}
                  // disabled={!isClearCompletedButtonActive}
                  size='small'
                  color='inherit'
                  classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                  style={{ margin: '0 4px'}}
                  disabled={ isLoading }
                >
                  <ArrowBackIcon />
                </IconButton>
              </span>
              
            </Tooltip>
            {isEditingName
              ? <NameInput name={name} isViewName invertColor onSave={handleNameSave} charLimit={Constants.ViewsCharLimit.VIEWNAME}/>
              : <>
                  <Tooltip title={selectedViewIsDirty ? 'View has unsaved changes' : ''}>
                    <Typography variant='subtitle2' className={classes.headerText} color='inherit' onDoubleClick={handleRenameClick}>{name}</Typography>
                  </Tooltip>
                  {selectedViewIsDirty &&
                    <Typography variant='subtitle2' style={{ fontSize: '1.2rem' }} color='inherit'>{'*'}</Typography>
                  }
                </>
            }
          </div>
          <div className={classes.headerRight}>
            <Tooltip title='Add Layer'>
              <span>
                <IconButton
                onClick={handleAddLayerClick}
                disabled={isLoading}
                size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                  <AddCircleIcon />
                </IconButton>
              </span>
            </Tooltip>
            {/* <Tooltip title='Edit Layers'>
              <IconButton
                // onClick={openSortMenu}
                color='inherit'
                classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                disabled={isLoading}
              >
                <SortIcon />
              </IconButton>
            </Tooltip> */}
            {canSave &&
              <Tooltip title={hasAnObject ? 'Save View' : 'Add a layer and object to enable saving'}>
                <span>
                  <IconButton
                  onClick={handleSaveClick}
                  disabled={saveDisabled || !selectedViewIsDirty}
                  size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            }
            <Tooltip title={hasAnObject ? 'Duplicate View' : 'Add a layer and object to enable duplicating'}>
              <span>
                <IconButton
                onClick={handleDuplicateViewClick}
                disabled={saveDisabled}
                size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                  <SvgIcon>
                    <path d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.11 3.9 21 5 21H12.81C12.45 20.38 12.2 19.7 12.08 19L12 19C10.34 19 9 17.66 9 16S10.34 13 12 13C12.68 13 13.34 13.23 13.87 13.65C15 12.59 16.46 12 18 12C19.05 12 20.09 12.28 21 12.81V7L17 3M15 9H5V5H15V9M17 14V17H14V19H17V22H19V19H22V17H19V14H17Z" />
                  </SvgIcon>
                </IconButton>
              </span>
            </Tooltip>
            {!isSystemView &&
              <IconButton
                className={classes.iconButton}
                onClick={openMoreMenu}
                disabled={isLoading}
                size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
              >
                <MoreHorizIcon />
              </IconButton>
            }
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={closeMoreMenu}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom'
              }}
            >
              <MenuItem onClick={handleBestFitView} disabled={selectedView?.objects?.every(obj => !obj.visible || obj.needsPositionSet == null || obj.needsPositionSet)}>Fit View on map</MenuItem>
              <MenuItem onClick={() => handleRenameClick(100)}>Rename View</MenuItem>
              {(isSystemView || isUserCreatedView || user.roles.administrator) &&
                <MenuItem onClick={handleDeleteView}>{isTemp ? 'Discard View' : 'Delete View'}</MenuItem>
              }
            </Menu>
            {isMobile && (
              <Tooltip title='Close Panel'>
                <span>
                  <IconButton
                    onClick={() => setIsSideBarOpen(false)}
                    size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                    <CloseIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <LoadingOverlayWrapper
          className={classes.overlay}
          active={isLoading}
          message={message}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="layer" type="LAYER" direction="vertical">
              {(provided) => (
                <div
                  id='viewContent'
                  style={{ overflow: 'auto', height: '100%' }}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {selectedView
                    ? selectedView.layers.length
                      ? selectedView.layers.map((layer, index) =>
                        <Layer
                          key={layer.id}
                          layer={layer}
                          index={index}
                          objectCountLimitOverrides={objectCountLimitOverrides}
                          setObjectCountLimitOverrides={setObjectCountLimitOverrides}
                        />
                      )
                      : <div className={classes.noLayersDiv}><Typography className={classes.noLayersText} variant='body2'>View contains no layers</Typography></div>
                    : <></>}
                  {provided.placeholder}
                </div>
                )
              }
            </Droppable>
          </DragDropContext>
          <div style={{ flexGrow: 1 }}/>
          <div className={classes.controlsContainer}>
            {hasAPropertyDataObject &&
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={<Switch checked={selectedView ? selectedView.propertyDataDisplay.visible : false} onChange={() => updateSelectedExploreView({ propertyDataDisplay: { ...selectedView?.propertyDataDisplay, visible: !selectedView?.propertyDataDisplay?.visible }})} />}
                  label={`${isMobile ? 'Enable' : 'Show'} property data grid/charts`}
                  disabled={isLoading || !selectedView}
                />
              </div>
            }
            <div style={{ display: 'flex' }}>
              <Tooltip title='Controls whether the map moves to the Home position when this view is first opened'>
                <span>
                  <FormControlLabel
                    control={<Switch checked={selectedView ? selectedView.useSavedPosition : false} onChange={() => updateSelectedExploreView({ useSavedPosition: !selectedView?.useSavedPosition })} />}
                    label="Use Home position on load"
                    disabled={isLoading || !selectedView}
                  />
                </span>
              </Tooltip>
              <div style={{ flexGrow: 1 }}/>
              <Tooltip title='Set the current map position as Home'>
                <span>
                  <IconButton className={classes.button} disabled={isLoading || !selectedView} onClick={handleSetPosition}>
                    <EditLocationIcon/>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Jump to the map's Home position">
                <span>
                  <IconButton className={classes.button} disabled={isLoading || !selectedView} onClick={handleJumpToPosition}>
                    <HomeIcon/>
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </LoadingOverlayWrapper>
      </div>
      <ViewObjectDetail
        selectedView={selectedView}
      />
      {/* {activeDrawingObject &&
        <div className={classes.drawingOverlay}>
          <Typography>Click on the map to place the object</Typography>
          <div>
            <Button variant='outlined' color='primary' className={classes.button} onClick={handleDrawingCancel}>Cancel</Button>
            <Button variant='contained' color='primary' className={classes.button} disabled={!activeDrawingObject.isPlaced} onClick={handleDrawingCreate}>Create</Button>
          </div>
        </div>
      } */}
      <DuplicateViewModal
        modalOpen={duplicateViewModalIsOpen}
        defaultValue={'Copy of ' + name}
        onClose={() => setDuplicateViewModalIsOpen(false)}
      />
    </>
  )
}

const styles = theme => ({
  viewContainer: {
    height: '100%',
    // width: 440,
    // position: 'absolute',
    backgroundColor: '#fff',
    // zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0
  },
  hiddenContainer: {
    height: 0
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 0',
    minWidth: 0
  },
  headerText: {
    fontSize: '1.2rem',
    // marginRight: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  headerRight: {
    //color: '#fff'
    marginRight: 4,
    display: 'flex'
  },
  iconButton: {
    padding: '5px',
  },
  noLayersDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px'
  },
  noLayersText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem'
  },
  overlay: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  drawingOverlay: {
    position: 'absolute',
    width: '440px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    zIndex: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.9)'
  },
  controlsContainer: {
    padding: '8px 24px',
    marginTop: -1,
    borderTop: '1px solid #dddddd',
    zIndex: 1
  }
});

const mapStateToProps = state => {
  const { appState, exploreViews, exploreMap } =  state;
  const { zoom, center, bounds } = exploreMap;
  const { selectedView, selectedObjectId } = exploreViews;
  return {
    user: appState.user,
    mapPosition: { zoomLevel: zoom, latitude: center?.lat, longitude: center?.lng, bounds },
    selectedView,
    selectedViewLoadingState: exploreViews.selectedViewLoadingState,
    selectedViewIsDirty: exploreViews.selectedViewIsDirty,
    hasSelectedObject: selectedView && selectedObjectId != null && selectedView.objects.find(object => object.id === selectedObjectId) != null,
    isMobile: appState.isMobile,
  }
};

const mapDispatchToProps = {
  updateSelectedExploreView,
  createExploreViewLayer,
  saveExploreView,
  setExploreSelectedViewIsOpen,
  setConfirmationModalState,
  deleteExploreView,
  cancelExploreDataFetch,
  setExploreLocation,
  appNotify,
  setIsSideBarOpen,
  fetchExplorePropertyData
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(View);
